/**
 * AXIS Action Registry — the declarative half (X11, IMPL-AX-01). Every `SignalActionKind` declares,
 * ONCE, its face verb, reversibility, hard-floor status (X12), graduation eligibility, and how to build
 * its learning/rule key. Cards (X13), the graduation engine (X3), and the recap are driven off these
 * declarations — no per-kind `if` blocks. The imperative half (executor/undo bindings) lives
 * server-side in `server-api/src/actions/registry.ts`, wrapping the existing write services.
 *
 * Hard-floor set at ship (X12): `send-draft` (email send), any Slack SEND (Slack rules graduate
 * auto-*prepare* only — X10/X30), delete/trash (not in the taxonomy — floored pre-emptively via this
 * registry having no such entry), and `unsubscribe` one-click-no-undo (the draft form here is NOT
 * floored). `create-draft-reply` is NOT floored — it graduates to silent-draft-in-Gmail (X30).
 * Financial/legal/security stakes are a classifier-forced `escalate`, enforced in the planner, not here.
 */
import type { Disposition } from '../engines/triage.js';
import type { SignalActionKind, SignalActionPayload } from '../signals/types.js';
import { trustRuleKey, type RuleGrain } from './autonomy.js';

export type ActionReversibility = 'reversible' | 'draft-only' | 'irreversible';

/** The declarative shape every action type registers (X11). */
export interface ActionTypeDecl {
  id: SignalActionKind;
  /** The card-face label (X13), e.g. 'Review draft' / 'Archive' / 'RSVP'. */
  verb: string;
  reversibility: ActionReversibility;
  /** X12 — never auto-executes regardless of earned trust. */
  floor: boolean;
  /** X3 — can a rule for this action ever be proposed for `auto`? Always false when floored. */
  graduationEligible: boolean;
  /** The disposition a rule/learning row for this action is recorded under. */
  disposition: Disposition;
  /** True for pure client navigation (open/link) — no learning, no rules, no execution server-side. */
  navigation?: boolean;
}

/**
 * The registry. EVERY `SignalActionKind` has an entry (asserted by the §8.1(3) invariant test) so new
 * actions plug in here once and cards/learning/graduation pick them up.
 */
export const ACTION_REGISTRY: Record<SignalActionKind, ActionTypeDecl> = {
  // Pure client navigation / lifecycle — no writes, no learning.
  'open-note': { id: 'open-note', verb: 'Open', reversibility: 'reversible', floor: false, graduationEligible: false, disposition: 'file', navigation: true },
  'open-panel': { id: 'open-panel', verb: 'Open', reversibility: 'reversible', floor: false, graduationEligible: false, disposition: 'file', navigation: true },
  link: { id: 'link', verb: 'Open', reversibility: 'reversible', floor: false, graduationEligible: false, disposition: 'file', navigation: true },
  snooze: { id: 'snooze', verb: 'Snooze', reversibility: 'reversible', floor: false, graduationEligible: false, disposition: 'file', navigation: true },
  dismiss: { id: 'dismiss', verb: 'Dismiss', reversibility: 'reversible', floor: false, graduationEligible: false, disposition: 'file', navigation: true },
  // Artifact creation. `create-note` is 'draft-only' (a local artifact the owner can delete; nothing
  // leaves Lapo), so it carries no registry undo; `create-task`'s undo closes the created task.
  'create-task': { id: 'create-task', verb: 'Add task', reversibility: 'reversible', floor: false, graduationEligible: true, disposition: 'task' },
  'create-note': { id: 'create-note', verb: 'Add note', reversibility: 'draft-only', floor: false, graduationEligible: true, disposition: 'file' },
  // Email writes (spec A5). Draft-creating kinds are 'draft-only': nothing a human receives, so not
  // floored — at `auto` the draft lands silently in Gmail (X30) and the send-gate is Gmail's own button.
  'create-draft-reply': { id: 'create-draft-reply', verb: 'Review draft', reversibility: 'draft-only', floor: false, graduationEligible: true, disposition: 'draft-reply' },
  'send-draft': { id: 'send-draft', verb: 'Send', reversibility: 'irreversible', floor: true, graduationEligible: false, disposition: 'draft-reply' },
  archive: { id: 'archive', verb: 'Archive', reversibility: 'reversible', floor: false, graduationEligible: true, disposition: 'ignore' },
  label: { id: 'label', verb: 'Label', reversibility: 'reversible', floor: false, graduationEligible: true, disposition: 'file' },
  'mark-read': { id: 'mark-read', verb: 'Mark read', reversibility: 'reversible', floor: false, graduationEligible: true, disposition: 'ignore' },
  'mark-spam': { id: 'mark-spam', verb: 'Spam', reversibility: 'reversible', floor: false, graduationEligible: false, disposition: 'spam' },
  // CULL — "Not spam" on an auto-archived message: unarchive + cancel the pending rule + record the
  // rescue. The human veto — never graduates.
  'rescue-spam': { id: 'rescue-spam', verb: 'Not spam', reversibility: 'reversible', floor: false, graduationEligible: false, disposition: 'spam' },
  // The v1 unsubscribe drafts an email (reversible-by-not-sending); a one-click no-undo unsubscribe
  // would be a distinct floored kind (X12b) — declared irreversible+floored pre-emptively via this row
  // staying 'draft-only'. The DRAFT form is graduation-eligible.
  unsubscribe: { id: 'unsubscribe', verb: 'Draft unsubscribe', reversibility: 'draft-only', floor: false, graduationEligible: true, disposition: 'unsubscribe' },
  // Calendar writes.
  rsvp: { id: 'rsvp', verb: 'RSVP', reversibility: 'reversible', floor: false, graduationEligible: true, disposition: 'rsvp' },
  'propose-times': { id: 'propose-times', verb: 'Review draft', reversibility: 'draft-only', floor: false, graduationEligible: true, disposition: 'propose-reschedule' },
  // Slack. The SEND is hard-floored (X12 — the universal send-floor: no human ever receives an
  // auto-SENT message); a graduated Slack rule auto-PREPARES the reply onto a one-tap card (X10/X30).
  'slack-send': { id: 'slack-send', verb: 'Send reply', reversibility: 'irreversible', floor: true, graduationEligible: false, disposition: 'draft-reply' },
  // REMIT (Bill Pay). `pay-bill` moves money → hard-floored + not AXIS-graduation-eligible (auto-pay is
  // the separate D16 prior-paid-under-cap mechanism, never AXIS trust). `cancel-bill` is the reversible
  // undo of an auto-pay window; neither is a learnable email/Slack disposition, so both file.
  'pay-bill': { id: 'pay-bill', verb: 'Pay bill', reversibility: 'irreversible', floor: true, graduationEligible: false, disposition: 'file' },
  'cancel-bill': { id: 'cancel-bill', verb: 'Cancel', reversibility: 'reversible', floor: false, graduationEligible: false, disposition: 'file' },
  // SETTLE-04 (DL-2) — accept a domain-suppression graduation proposal. Sets a `domain:` spam policy;
  // reversible (remove the rule in "What Lapo learned"). Never AXIS-graduates (it IS a suppression rule).
  'suppress-domain': { id: 'suppress-domain', verb: 'Suppress domain', reversibility: 'reversible', floor: false, graduationEligible: false, disposition: 'spam' },
};

/** Look up an action's declaration. Total over SignalActionKind (invariant-tested). */
export function actionDecl(kind: SignalActionKind): ActionTypeDecl {
  return ACTION_REGISTRY[kind];
}

/**
 * The learning/rule key for an action riding a signal (X11 `learningKeyOf`): sender-grain against the
 * payload's sender, keyed by the action's disposition. Returns null for navigation actions and when no
 * sender/subject is available (nothing to learn on).
 */
export function learningKeyOf(kind: SignalActionKind, payload?: SignalActionPayload): string | null {
  const decl = ACTION_REGISTRY[kind];
  if (!decl || decl.navigation) return null;
  const sender = payload?.sender?.toLowerCase().trim();
  if (!sender) return null;
  return trustRuleKey('sender', sender, decl.disposition);
}

/** The rule key at an arbitrary grain for an action + subject (card ⋯ shortcuts, category rules). */
export function ruleKeyFor(kind: SignalActionKind, grain: RuleGrain, subject: string): string | null {
  const decl = ACTION_REGISTRY[kind];
  if (!decl || decl.navigation) return null;
  return trustRuleKey(grain, subject, decl.disposition);
}

/** Whether a kind may EVER auto-execute (X12 floor + eligibility, checked before any auto path). */
export function isAutoExecutable(kind: SignalActionKind): boolean {
  const decl = ACTION_REGISTRY[kind];
  return Boolean(decl && !decl.floor && decl.graduationEligible && !decl.navigation);
}
